import React, { useState, useEffect } from 'react';
import { ColorSchemeEditorProps, ColorScheme } from './types';
import { DEFAULT_COLOR_VARIABLES, getDefaultColorScheme } from './colorVariables';
import { ColorPicker } from './ColorPicker';
import { ColorSchemePreview } from './ColorSchemePreview';
import { useTheme } from '../../context/ThemeContext';

/**
 * Main color scheme editor component
 */
export const ColorSchemeEditor: React.FC<ColorSchemeEditorProps & { colorSearch?: string }> = ({
  currentScheme,
  onSave,
  onCancel,
  compact = false,
  colorSearch = ''
}) => {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState<'editor' | 'preview' | 'export'>('editor');
  const [previewMode, setPreviewMode] = useState<'light' | 'dark'>('light');
  const [activeCategory, setActiveCategory] = useState<string>('main');
  
  // Initialize with current scheme or default
  const [editingScheme, setEditingScheme] = useState<ColorScheme>(() => {
    if (currentScheme) {
      return currentScheme;
    }
    
    const defaultColors = getDefaultColorScheme();
    return {
      id: 'custom-' + Date.now(),
      name: 'Custom Color Scheme',
      description: 'A custom color scheme configuration',
      colors: defaultColors
    };
  });

  const categories = [
    { id: 'main', name: 'Main Colors', icon: '🎨' },
    { id: 'secondary', name: 'Secondary', icon: '🎯' },
    { id: 'accent', name: 'Accent', icon: '✨' },
    { id: 'background', name: 'Backgrounds', icon: '🖼️' },
    { id: 'text', name: 'Text', icon: '📝' },
    { id: 'border', name: 'Borders', icon: '📐' },
    { id: 'status', name: 'Status', icon: '🚦' },
    { id: 'form', name: 'Forms', icon: '📋' },
    { id: 'debug', name: 'Debug', icon: '🐛' }
  ];
  const handleColorChange = (varName: string, mode: 'light' | 'dark', value: string) => {
    setEditingScheme(prev => ({
      ...prev,
      colors: {
        ...prev.colors,
        [mode]: {
          ...prev.colors[mode],
          [varName]: value
        }
      }
    }));
    
    // Auto-apply the change if we're in the same mode as the current theme
    const root = document.documentElement;
    const isDark = root.classList.contains('dark');
    const isCurrentMode = (isDark && mode === 'dark') || (!isDark && mode === 'light');
    
    if (isCurrentMode) {
      root.style.setProperty(varName, value);
    }
  };

  const handleSchemeNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingScheme(prev => ({
      ...prev,
      name: e.target.value
    }));
  };

  const handleSchemeDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditingScheme(prev => ({
      ...prev,
      description: e.target.value
    }));
  };

  const handleSave = () => {
    if (onSave) {
      onSave(editingScheme);
    }
  };
  
  const handleReset = () => {
    const defaultColors = getDefaultColorScheme();
    setEditingScheme(prev => ({
      ...prev,
      colors: defaultColors
    }));
    
    // Also apply the reset immediately for real-time feedback
    const root = document.documentElement;
    const isDark = root.classList.contains('dark');
    const colors = isDark ? defaultColors.dark : defaultColors.light;
    
    Object.entries(colors).forEach(([variable, value]) => {
      root.style.setProperty(variable, value);
    });
  };

  const generateCssOutput = () => {
    const { light, dark } = editingScheme.colors;
    
    let css = `/* ${editingScheme.name} */\n`;
    css += `/* ${editingScheme.description || 'Custom color scheme'} */\n\n`;
    
    css += `@layer base {\n`;
    css += `  :root {\n`;
    
    // Light mode colors
    Object.entries(light).forEach(([varName, value]) => {
      css += `    ${varName}: ${value};\n`;
    });
    
    css += `  }\n\n`;
    
    css += `  .dark {\n`;
    
    // Dark mode colors
    Object.entries(dark).forEach(([varName, value]) => {
      css += `    ${varName}: ${value};\n`;
    });
    
    css += `  }\n`;
    css += `}\n`;
    
    return css;
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('CSS copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Failed to copy CSS to clipboard');
    }
  };
  const filteredVariables = DEFAULT_COLOR_VARIABLES.filter(
    variable =>
      variable.category === activeCategory &&
      (
        !colorSearch ||
        variable.name.toLowerCase().includes(colorSearch.toLowerCase()) ||
        (variable.displayName && variable.displayName.toLowerCase().includes(colorSearch.toLowerCase())) ||
        (variable.description && variable.description.toLowerCase().includes(colorSearch.toLowerCase())) ||
        (editingScheme.colors.light[variable.name] && editingScheme.colors.light[variable.name].toLowerCase().includes(colorSearch.toLowerCase())) ||
        (editingScheme.colors.dark[variable.name] && editingScheme.colors.dark[variable.name].toLowerCase().includes(colorSearch.toLowerCase()))
      )
  );
  return (
    <div className={`color-scheme-editor bg-background text-foreground ${compact ? 'space-y-2' : 'space-y-4'}`}>
      {/* Header */}
      <div className={`card-container ${compact ? 'p-2' : ''}`}><div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
          <h2 className="text-lg font-bold text-foreground">Color Scheme Editor</h2>
          <div className="flex flex-wrap gap-2">
            <button 
              onClick={handleReset}
              className="auth-button-secondary text-xs px-3 py-1"
            >
              Reset
            </button>
            <button 
              onClick={handleSave}
              className="auth-button-primary text-xs px-3 py-1"
            >
              Save
            </button>
            {onCancel && (
              <button 
                onClick={onCancel}
                className="auth-button-danger text-xs px-3 py-1"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
        
        {/* Scheme Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="form-group">
            <label className="form-label text-xs">Scheme Name</label>
            <input
              type="text"
              value={editingScheme.name}
              onChange={handleSchemeNameChange}
              className="form-input text-sm py-1"
              placeholder="Enter scheme name"
            />
          </div>
          <div className="form-group">
            <label className="form-label text-xs">Description</label>
            <textarea
              value={editingScheme.description || ''}
              onChange={handleSchemeDescriptionChange}
              className="form-input text-sm py-1"
              placeholder="Describe your color scheme"
              rows={1}
            />
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="card-container">
        <div className="auth-tab-container">
          <button
            onClick={() => setActiveTab('editor')}
            className={`auth-tab text-sm px-3 py-1 ${activeTab === 'editor' ? 'auth-tab-active' : ''}`}
          >
            🎨 Editor
          </button>
          <button
            onClick={() => setActiveTab('preview')}
            className={`auth-tab text-sm px-3 py-1 ${activeTab === 'preview' ? 'auth-tab-active' : ''}`}
          >
            👁️ Preview
          </button>
          <button
            onClick={() => setActiveTab('export')}
            className={`auth-tab text-sm px-3 py-1 ${activeTab === 'export' ? 'auth-tab-active' : ''}`}
          >
            📋 Export CSS
          </button>
        </div>
      </div>      {/* Editor Tab */}
      {activeTab === 'editor' && (
        <div className="space-y-4">          {/* Real-time Preview Info */}
          <div className="auth-message auth-message-info text-sm">
            <strong>🎨 Real-time Preview:</strong> Changes apply instantly as you edit colors! Use the "Test Theme" button in the header to apply current changes.
          </div>
          
          <div className={`grid grid-cols-1 ${compact ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} ${compact ? 'gap-2' : 'gap-4'}`}>
            {/* Category Sidebar */}
            <div className={`card-container ${compact ? 'p-2 lg:col-span-1' : 'lg:col-span-1'}`}>
              <h3 className="form-section-title text-sm mb-2">Categories</h3>
              <div className="space-y-1">
                {categories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`w-full text-left p-2 rounded-lg transition-colors text-sm ${
                      activeCategory === category.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                    }`}
                  >
                    <span className="mr-2">{category.icon}</span>
                    {category.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Color Editor */}
            <div className={`card-container ${compact ? 'p-2 lg:col-span-4' : 'lg:col-span-3'}`}>
              <h3 className="form-section-title text-sm mb-3">
                {categories.find(c => c.id === activeCategory)?.name} Colors
              </h3>
              <div className="space-y-4">
                {filteredVariables.map(variable => (                  <ColorPicker
                    key={variable.name}
                    variable={variable}
                    lightValue={editingScheme.colors.light[variable.name] || variable.defaultLight}
                    darkValue={editingScheme.colors.dark[variable.name] || variable.defaultDark}
                    onLightChange={(value) => handleColorChange(variable.name, 'light', value)}
                    onDarkChange={(value) => handleColorChange(variable.name, 'dark', value)}
                    compact={compact}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}{/* Preview Tab */}
      {activeTab === 'preview' && (
        <div className="card-container">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Live Preview</h3>
            <div className="auth-tab-container">
              <button
                onClick={() => setPreviewMode('light')}
                className={`auth-tab text-sm px-3 py-1 ${previewMode === 'light' ? 'auth-tab-active' : ''}`}
              >
                ☀️ Light
              </button>
              <button
                onClick={() => setPreviewMode('dark')}
                className={`auth-tab text-sm px-3 py-1 ${previewMode === 'dark' ? 'auth-tab-active' : ''}`}
              >
                🌙 Dark
              </button>
            </div>
          </div>
          
          <ColorSchemePreview 
            scheme={editingScheme} 
            mode={previewMode}
          />
        </div>
      )}

      {/* Export Tab */}
      {activeTab === 'export' && (
        <div className="card-container">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold">Export CSS</h3>
            <button
              onClick={() => copyToClipboard(generateCssOutput())}
              className="auth-button-primary text-sm px-3 py-1"
            >
              📋 Copy CSS
            </button>
          </div>
          
          <div className="auth-message auth-message-info mb-3 text-sm">
            <strong>Instructions:</strong> Copy the CSS below and paste it into your <code>src/index.css</code> file to replace the existing color variables.
          </div>
          
          <div className="bg-debug-background text-debug-foreground p-3 rounded-lg overflow-auto max-h-96">
            <pre className="text-xs whitespace-pre-wrap font-mono">
              {generateCssOutput()}
            </pre>
          </div>
          
          <div className="mt-3 p-3 bg-warning/10 border border-warning rounded-lg">
            <h4 className="font-semibold text-warning-foreground mb-2 text-sm">⚠️ Important Notes:</h4>
            <ul className="text-xs text-warning-foreground space-y-1 list-disc list-inside">
              <li>Always backup your existing <code>src/index.css</code> before making changes</li>
              <li>This will replace the entire color system in your application</li>
              <li>Test thoroughly in both light and dark modes before deploying</li>
              <li>Consider accessibility guidelines when choosing colors</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
